import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { isAdminSelector, isChatAllowedSelector } from "../Auth/authSlice";
import { NavigationBar } from "../NavigationBar";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getProfile, setJwtToken } from "../Auth/authSlice";
import { LOCAL_STORAGE_KEYS } from "../../lib/localStorage";
import { AddDriverModal, EditDriverModal, DeleteDriverConfirmationModal } from "../Drivers";
import { CreateScheduleModal } from "../Schedules";
import {
  AddDumpYardModal,
  EditDumpYardModal,
  UpdateInstructionsModal,
} from "../DumpYards";
import {
  UpdateOrderModal,
  DeleteOrderModal,
  PickupInstructionsModal,
  OrderDetailsModal,
  UpdatePaymentTypeModal,
} from "../Orders";
import { DeleteDumpYardConfirmationModal } from "../DumpYards/DeleteDumpYardConfirmationModal/DeleteDumpYardConfirmationModal";
import { EditPunchCardModal, CreatePunchCardModal } from "../PunchCards/modals";
import { AddInvoiceModal } from "../Orders/modals";
import {
  AddDumpsterModal,
  DeleteDumpsterConfirmationModal,
  EditDumpsterModal,
  SetLocationModal,
} from "../Dumpsters";
import {
  AddVehicleModal,
  EditVehicleModal,
  DeleteVehicleConfirmationModal,
} from "../Vehicles";
import { ChatPanel } from "../Chat/ChatPanel";
import { ChatLauncherFab } from "../Chat/ChatLauncherFab";
import { getDriversV2 } from "../DriversV2/driversV2Slice";
import { getDashboardOrderStats } from "../Orders/ordersSlice";
import { getDashboardScheduleStats } from "../Schedules/scheduleSlice";
import { getOrdersDashboard } from "../ReceivedOrders/receivedOrdersSlice";
import { getVehicles } from "../Vehicles/vehiclesSlice";
import { getDumpYardTemplates } from "../DumpYards/dumpYardsSlice";

import "./Layout.scss";

export function Layout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const jwtToken = useAppSelector((state) => state.auth.jwtToken);
  const getProfileLoading = useAppSelector(
    (state) => state.auth.getProfile.loading
  );
  const didGetProfileFail = useAppSelector(
    (state) => !!state.auth.getProfile.error
  );
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = useAppSelector(isAdminSelector);
  const isChatAllowed = useAppSelector(isChatAllowedSelector);

  React.useEffect(() => {
    const jwtToken = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN);
    dispatch(setJwtToken({ jwtToken: jwtToken || undefined }));
  }, []);

  React.useEffect(() => {
    if (!!jwtToken) {
      dispatch(getProfile());

      // Only redirect to dashboard if on auth pages or root
      const isAuthPage = location.pathname.startsWith("/auth") || location.pathname === "/";
      if (isAuthPage) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [jwtToken]);

  React.useEffect(() => {
    if (!getProfileLoading && didGetProfileFail) {
      dispatch(setJwtToken({ jwtToken: undefined }));
      navigate("/auth/login", { replace: true });
    }
  }, [getProfileLoading, didGetProfileFail]);

  // Fetch app data when admin is authenticated
  React.useEffect(() => {
    if (user && isAdmin) {
      dispatch(getDriversV2());
      dispatch(getDashboardOrderStats());
      dispatch(getDashboardScheduleStats());
      dispatch(getOrdersDashboard());
      dispatch(getVehicles());
      dispatch(getDumpYardTemplates());
    }
  }, [user, isAdmin]);

  const isDevelopment = React.useMemo(
    () =>
      !process.env.REACT_APP_ENVIRONMENT ||
      process.env.REACT_APP_ENVIRONMENT === "development",
    [process.env.REACT_APP_ENVIRONMENT]
  );

  return (
    <>
      {isDevelopment && (
        <div className="not-production-banner">This is not production!</div>
      )}
      <NavigationBar />

      <AddDriverModal />
      <EditDriverModal />
      <CreateScheduleModal />
      <AddDumpYardModal />
      <EditDumpYardModal />
      <UpdateInstructionsModal />
      <UpdateOrderModal />
      <OrderDetailsModal />
      <DeleteDumpYardConfirmationModal />
      <DeleteDriverConfirmationModal />
      <PickupInstructionsModal />
      <UpdatePaymentTypeModal />

      {isAdmin && (
        <>
          <DeleteOrderModal />
          <EditPunchCardModal />
          <CreatePunchCardModal />
          <AddInvoiceModal />
          <AddDumpsterModal />
          <DeleteDumpsterConfirmationModal />
          <EditDumpsterModal />
          <SetLocationModal />
          <AddVehicleModal />
          <EditVehicleModal />
          <DeleteVehicleConfirmationModal />
          {isChatAllowed && <ChatPanel />}
          {isChatAllowed && <ChatLauncherFab />}
        </>
      )}

      <Outlet />
    </>
  );
}
